import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Bell, Check, ShoppingBag, Calendar } from 'lucide-react';

const API = '/api';

/**
 * CAMPANITA VERDE — ventas / acciones de cliente (Canal cliente).
 * Verde = ventas de fisio reservadas desde el canal, pendientes de gestionar.
 * "Gestionado" -> pasa a gris (abajo). Al pasar el día de la cita, desaparece
 * (lo filtra el backend en /canal-reservas/avisos).
 */
const GreenNotificationBell = () => {
  const [avisos, setAvisos] = useState([]);
  const [open, setOpen] = useState(false);
  const [shake, setShake] = useState(false);
  const prevPend = useRef(0);
  const sonidoOk = useRef(false);
  const ref = useRef(null);

  const pendientes = avisos.filter((a) => a.estado_aviso === 'pendiente');
  const gestionados = avisos.filter((a) => a.estado_aviso === 'gestionado');

  const fetchAvisos = async () => {
    try {
      const res = await axios.get(`${API}/canal-reservas/avisos`);
      const list = res.data?.avisos || [];
      const nPend = list.filter((a) => a.estado_aviso === 'pendiente').length;
      if (nPend > prevPend.current && prevPend.current !== 0) {
        setShake(true);
        setTimeout(() => setShake(false), 1200);
        beep();
      }
      prevPend.current = nPend;
      setAvisos(list);
    } catch (e) {
      /* silencioso: la campanita no debe romper la barra */
    }
  };

  useEffect(() => {
    fetchAvisos();
    const t = setInterval(fetchAvisos, 60000);
    // El primer gesto del usuario habilita el sonido (los navegadores lo exigen).
    const habilitar = () => { sonidoOk.current = true; };
    window.addEventListener('click', habilitar, { once: true });
    return () => { clearInterval(t); window.removeEventListener('click', habilitar); };
  }, []);

  useEffect(() => {
    const fuera = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', fuera);
    return () => document.removeEventListener('mousedown', fuera);
  }, []);

  const beep = () => {
    if (!sonidoOk.current) return;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = 'sine';
      o.frequency.value = 880;
      g.gain.value = 0.06;
      o.connect(g); g.connect(ctx.destination);
      o.start();
      setTimeout(() => { o.stop(); ctx.close(); }, 180);
    } catch (e) { /* sin sonido */ }
  };

  const gestionar = async (id) => {
    try {
      await axios.post(`${API}/canal-reservas/${id}/gestionar-aviso`);
      fetchAvisos();
    } catch (e) { /* noop */ }
  };

  const fmtFecha = (iso) => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
  };

  const fila = (a, gris) => (
    <div key={a.id} className={`flex items-start gap-2 px-3 py-2 border-b ${gris ? 'bg-gray-50 opacity-70' : 'bg-emerald-50'}`}>
      <ShoppingBag className={`w-4 h-4 mt-0.5 flex-shrink-0 ${gris ? 'text-gray-400' : 'text-emerald-600'}`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-800 truncate">{a.cliente_nombre || 'Cliente'}</p>
        <p className="text-xs text-gray-600">{a.tratamiento_nombre} · {fmtFecha(a.fecha)} {a.hora_inicio}</p>
        <p className="text-[11px] text-gray-400">{(a.profesional_nombre || '').split(' ')[0]}</p>
      </div>
      {!gris && (
        <button
          onClick={() => gestionar(a.id)}
          className="flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-100 hover:bg-emerald-200 px-2 py-1 rounded"
        >
          <Check className="w-3 h-3" /> Gestionado
        </button>
      )}
    </div>
  );

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((o) => !o)} className="relative p-1" title="Ventas de cliente (Canal)">
        <Bell className={`w-6 h-6 ${pendientes.length > 0 ? 'text-emerald-500' : 'text-gray-400'} ${shake ? 'animate-bounce' : ''}`} />
        {pendientes.length > 0 && (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-emerald-500 rounded-full">
            {pendientes.length}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto bg-white rounded-lg shadow-xl border border-gray-200 z-50">
          <div className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white text-sm font-semibold rounded-t-lg sticky top-0">
            <ShoppingBag className="w-4 h-4" /> Ventas de cliente (Canal)
          </div>
          {avisos.length === 0 && (
            <p className="px-4 py-6 text-sm text-gray-400 text-center">Sin ventas pendientes</p>
          )}
          {pendientes.map((a) => fila(a, false))}
          {gestionados.length > 0 && (
            <>
              <div className="flex items-center gap-1 px-4 py-1 bg-gray-100 text-[11px] text-gray-500 font-medium">
                <Calendar className="w-3 h-3" /> Gestionadas
              </div>
              {gestionados.map((a) => fila(a, true))}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default GreenNotificationBell;
